/**
 * PublicFilesPage — `/public-files`
 *
 * Browse + download the ACC / US Air document catalogue (MySQL `Email Data`).
 * Role-aware: guests and customers only see files flagged
 * `AvailableForCustomer='1'`. Staff see everything and can upload new files.
 *
 * Layout:
 *   • Top: search + class filter
 *   • Body: grouped by File Class, each row has Download + Send
 *   • Admin-only: "Add file" button in the header that opens an upload form
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Folders, MagnifyingGlass, Funnel, DownloadSimple,
  PaperPlaneTilt, Plus, Trash, Spinner, Tag, FileText, FileImage,
  FilePdf, FileVideo, CheckCircle, Warning, CloudArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AccountMenu from "@/components/AccountMenu";
import SendPublicFilesModal from "@/components/SendPublicFilesModal";
import { useT } from "@/lib/i18n";
import {
  fetchPublicFiles, fetchPublicFileClasses, publicFileDownloadUrl,
  adminCreatePublicFile, adminDeletePublicFile,
} from "@/lib/api";

function fileIconFor(filename) {
  const lower = (filename || "").toLowerCase();
  if (lower.match(/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|svg)$/)) return FileImage;
  if (lower.match(/\.(mp4|mov|avi|wmv|mkv|webm|m4v|mpeg)$/)) return FileVideo;
  if (lower.endsWith(".pdf")) return FilePdf;
  return FileText;
}

export default function PublicFilesPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const isAdmin = !!user?.is_admin;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendSeed, setSendSeed] = useState([]);
  const [showUpload, setShowUpload] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [f, c] = await Promise.all([
        fetchPublicFiles({ limit: 500 }),
        fetchPublicFileClasses(),
      ]);
      setFiles(f?.items || []);
      setClasses((c?.items || []).map((x) => x.file_class));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return files.filter((f) => {
      if (cls && f.file_class !== cls) return false;
      if (!ql) return true;
      return (
        f.file_type.toLowerCase().includes(ql) ||
        (f.explanation || "").toLowerCase().includes(ql) ||
        f.file_class.toLowerCase().includes(ql)
      );
    });
  }, [files, q, cls]);

  const grouped = useMemo(() => {
    const out = new Map();
    for (const f of filtered) {
      const key = f.file_class || "Uncategorized";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(f);
    }
    return Array.from(out.entries());
  }, [filtered]);

  const openSend = (fileId) => {
    setSendSeed(fileId ? [fileId] : []);
    setSendOpen(true);
  };

  const isGuest = !user?.email || String(user.email).endsWith("@local.guest");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button
          data-testid="public-files-back"
          onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title="Home"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Folders size={20} weight="fill" className="text-blue-700" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="public-files-title">
          {t("public_files.title")}
        </h1>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          · {files.length} {t("public_files.docs_count")}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {isAdmin && (
            <button
              data-testid="public-files-add-btn"
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-bold rounded-sm bg-violet-600 text-white hover:bg-violet-700"
            >
              <Plus size={12} weight="bold" /> {t("public_files.add_file")}
            </button>
          )}
          {!isGuest && (
            <button
              data-testid="public-files-send-header-btn"
              onClick={() => openSend(null)}
              className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-bold rounded-sm bg-blue-700 text-white hover:bg-blue-800"
            >
              <PaperPlaneTilt size={12} weight="bold" /> {t("public_files.send")}
            </button>
          )}
          {user?.email && <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              data-testid="public-files-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("public_files.search_placeholder")}
              className="w-full h-9 pl-8 pr-2 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
            />
          </div>
          <div className="relative">
            <Funnel size={13} weight="bold" className="absolute left-2.5 top-2.5 text-slate-400" />
            <select
              data-testid="public-files-class-filter"
              value={cls}
              onChange={(e) => setCls(e.target.value)}
              className="h-9 pl-8 pr-6 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
            >
              <option value="">{t("public_files.all_classes")} ({classes.length})</option>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {loading && (
          <div className="text-xs text-slate-500 flex items-center gap-2 py-4">
            <Spinner size={14} className="animate-spin" /> Loading…
          </div>
        )}
        {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</div>}
        {!loading && grouped.length === 0 && (
          <div data-testid="public-files-empty" className="text-xs text-slate-500 text-center py-10">
            {t("public_files.empty")}
          </div>
        )}

        {!loading && grouped.map(([classKey, items]) => (
          <section key={classKey} className="bg-white border border-slate-200 rounded-md p-4">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5 mb-3">
              <Tag size={10} weight="bold" /> {classKey}
              <span className="text-slate-300 normal-case tracking-normal font-normal">· {items.length}</span>
            </h3>
            <ul className="divide-y divide-slate-100">
              {items.map((f) => {
                const Icon = fileIconFor(f.file);
                return (
                  <li
                    key={f.id}
                    data-testid={`public-file-list-row-${f.id}`}
                    className="flex items-start gap-3 py-2.5"
                  >
                    <Icon size={18} weight="bold" className="text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">{f.file_type}</span>
                        {!f.available_for_customer && (
                          <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1 py-0.5 rounded-sm">
                            {t("public_files.internal_chip")}
                          </span>
                        )}
                        {f.allowed_with_quote && (
                          <span className="text-[9px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-1 py-0.5 rounded-sm">
                            {t("public_files.quote_attachable_chip")}
                          </span>
                        )}
                      </div>
                      {f.explanation && (
                        <div className="text-[11px] text-slate-500 truncate">{f.explanation}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <a
                        data-testid={`public-file-download-${f.id}`}
                        href={publicFileDownloadUrl(f.id)}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100"
                        title={t("public_files.download")}
                      >
                        <DownloadSimple size={11} weight="bold" /> {t("public_files.download")}
                      </a>
                      {!isGuest && (
                        <button
                          data-testid={`public-file-send-${f.id}`}
                          onClick={() => openSend(f.id)}
                          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800"
                        >
                          <PaperPlaneTilt size={11} weight="bold" /> {t("public_files.send")}
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          data-testid={`public-file-delete-${f.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete "${f.file_type}"? This removes the MySQL row and the local copy.`)) return;
                            try {
                              await adminDeletePublicFile(f.id);
                              toast.success("Deleted");
                              reload();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Delete failed");
                            }
                          }}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                          title={t("action.delete", "Delete")}
                        >
                          <Trash size={11} weight="bold" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </main>

      <SendPublicFilesModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        preselectedFileIds={sendSeed}
      />

      {showUpload && isAdmin && (
        <UploadPublicFileModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
          existingClasses={classes}
        />
      )}
    </div>
  );
}

function UploadPublicFileModal({ onClose, onUploaded, existingClasses }) {
  const t = useT();
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [fileType, setFileType] = useState("");
  const [fileClass, setFileClass] = useState("");
  const [explanation, setExplanation] = useState("");
  const [availForCustomer, setAvailForCustomer] = useState(true);
  const [allowedWithQuote, setAllowedWithQuote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    if (!fileType.trim()) {
      toast.error("File type is required");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      await adminCreatePublicFile(file, {
        file_type: fileType.trim(),
        file_class: fileClass.trim(),
        explanation: explanation.trim(),
        available_for_customer: availForCustomer,
        allowed_with_quote: allowedWithQuote,
        display_name: displayName.trim() || file.name,
      }, setProgress);
      toast.success("File added to Public Files");
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="public-file-upload-modal"
      className="fixed inset-0 z-[300] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/40" onClick={busy ? null : onClose} />
      <div className="relative bg-white w-[92vw] max-w-lg rounded-md border border-slate-200 shadow-xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <CloudArrowUp size={16} weight="bold" className="text-violet-600" />
          <h2 className="text-sm font-bold font-['Chivo'] flex-1">{t("public_files.add_file_modal_title", "Add a new Public File")}</h2>
          <button
            data-testid="upload-modal-close"
            onClick={onClose}
            disabled={busy}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-50"
          >
            ✕
          </button>
        </div>
        <div className="p-4 space-y-3">
          <input
            data-testid="upload-file-input"
            type="file"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
              if (f && !displayName) setDisplayName(f.name);
              if (f && !fileType) setFileType(f.name.replace(/\.[^.]+$/, ""));
            }}
            className="w-full text-xs"
          />
          <Field
            label={t("public_files.display_name_label", "Display name (shown to recipients)")} testid="upload-display-name"
            value={displayName} onChange={setDisplayName} placeholder={file?.name || "e.g. ACC_Screw_Compressor_Brochure.pdf"}
          />
          <Field
            label={t("public_files.file_type_label", "File type (title)")} testid="upload-file-type"
            value={fileType} onChange={setFileType} placeholder={t("public_files.file_type_placeholder", "e.g. ACC Screw Compressor Brochure")} required
          />
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              {t("public_files.file_class_label", "File Class")}
            </label>
            <input
              data-testid="upload-file-class"
              list="upload-existing-classes"
              value={fileClass}
              onChange={(e) => setFileClass(e.target.value)}
              placeholder={t("public_files.file_class_placeholder", "e.g. Catalogue Online (Email)")}
              className="w-full h-8 px-2 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
            />
            <datalist id="upload-existing-classes">
              {existingClasses.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Explanation
            </label>
            <textarea
              data-testid="upload-explanation"
              rows={2}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              className="w-full px-2 py-1.5 text-xs rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 resize-none"
            />
          </div>
          <div className="flex items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-1.5">
              <input
                data-testid="upload-avail-for-customer"
                type="checkbox"
                checked={availForCustomer}
                onChange={(e) => setAvailForCustomer(e.target.checked)}
              />
              Visible to customers / guests
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                data-testid="upload-quote-attachable"
                type="checkbox"
                checked={allowedWithQuote}
                onChange={(e) => setAllowedWithQuote(e.target.checked)}
              />
              Attachable to quotes
            </label>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5 text-[10px] text-amber-800">
            <b>Storage:</b> the file is stored in our local server and served from there. Back-up to
            Google Drive requires an additional OAuth scope — ask an engineer to wire
            it when needed.
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              data-testid="upload-cancel-btn"
              onClick={onClose}
              disabled={busy}
              className="h-8 px-3 text-xs font-bold rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {t("action.cancel", "Cancel")}
            </button>
            <button
              data-testid="upload-submit-btn"
              onClick={handleUpload}
              disabled={busy || !file || !fileType.trim()}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-bold rounded-sm bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-300"
            >
              {busy ? (
                <><Spinner size={12} className="animate-spin" /> {progress < 100 ? `${progress}%` : "Saving…"}</>
              ) : (
                <><CheckCircle size={12} weight="bold" /> Upload</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, required }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-8 px-2 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
      />
    </div>
  );
}
